import { HStack, Image, Skeleton, Text, VStack } from '@chakra-ui/react';
import React from 'react';
import { useQueryClient } from 'react-query';
import { Navigate, useSearchParams } from 'react-router-dom';

import { getRequestHeaders } from '../../../../../framework/apis/base/header';
import { AppError } from '../../../../../framework/models/errors';
import { getToken } from '../../../../../framework/utils/KeycloakUtils';
import { TypeUtils } from '../../../../../framework/utils/UtilityTypes';
import { useCheckArea } from '../../../../components/Check';
import {
  BackButton,
  DecideButton,
  DownloadButton,
} from '../../../../components/StyledButton';
import { pageNames, pagePaths } from '../../../../constants/Page';
import { useGlobalStateMachine } from '../../../../GlobalStore';
import {
  DateChangerState,
  generateInitialState,
  useDateChangers,
} from '../../../../hooks/DateChangers';
import { useEngeneNavigate } from '../../../../hooks/navigate';
import {
  DownloadAttachedResult,
  useGetAttachedFile,
} from '../../../../hooks/queries/attachedFile';
import { usePostDeadlineSearch } from '../../../../hooks/queries/deadline';
import {
  OUTSOURCER_INVOICE_CONTEXT_NAMES,
  usePostOutsourcerInvoiceId,
  usePostOutsourcerInvoicePublishConfirm,
} from '../../../../hooks/queries/outsourcerInvoice';
import useToastHook from '../../../../hooks/ToastHook';
import { DeadlineMode } from '../../../../models/gen/deadlineMode';
import { FileKind } from '../../../../models/gen/fileKind';
import { OutsourceInvoiceSearchIdResult } from '../../../../models/gen/outsourceInvoiceSearchIdResult';
import { FileUtils } from '../../../../utils/FileUtils';
import { useSizeContext } from '../../../SizeProvider';
import Wireframe from '../../../wireframe/Wireframe';

/**
 * 請求画面
 *
 * - URL `/outsourcer/invoice`
 * - Query: なし
 *
 * @returns
 */
const Invoice: React.FunctionComponent = () => {
  const { actions, state } = useGlobalStateMachine();
  if (
    TypeUtils.isNil(state.userInfo) ||
    TypeUtils.isNilString(state.userInfo.outsourcer_code)
  ) {
    return <Navigate to={pagePaths.ROOT} />;
  }

  const sizes = useSizeContext();
  const queryClient = useQueryClient();
  const navigate = useEngeneNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToastHook();
  const {
    MonthChanger,
    setState,
    state: monthState,
  } = useDateChangers({
    initialState:
      generateInitialState(searchParams, ['year', 'month']) ||
      state.pageInfo.INVOICE?.dateState,
  });
  const { CheckArea, checked, off } = useCheckArea();

  const postOutsourcerInvoiceId = usePostOutsourcerInvoiceId();
  const { data: deadlines, refetch: refetchDeadlines } = usePostDeadlineSearch({
    modes: [DeadlineMode.Invoice],
  });
  const data = queryClient.getQueryData<OutsourceInvoiceSearchIdResult>(
    OUTSOURCER_INVOICE_CONTEXT_NAMES.POST_OUTSOURCER_INVOICE_ID
  );
  const setData = React.useCallback(
    (result: OutsourceInvoiceSearchIdResult) =>
      queryClient.setQueryData(
        OUTSOURCER_INVOICE_CONTEXT_NAMES.POST_OUTSOURCER_INVOICE_ID,
        result
      ),
    [queryClient]
  );
  const onChangeChanger = (dateState: DateChangerState) => {
    actions.updatePageInfo({ INVOICE: { dateState } });
    off();
    postOutsourcerInvoiceId.mutate(
      {
        year: dateState.year,
        month: dateState.month,
        outsourcerCode: state.userInfo!.outsourcer_code!,
      },
      {
        onSuccess: (result) => {
          if (!TypeUtils.isNil(result)) {
            setData(result);
          }
        },
        onError: (error) =>
          toast(
            error instanceof AppError ? error : '請求情報の取得に失敗しました'
          ),
      }
    );
  };
  const postOutsourcerInvoicePublishConfirm =
    usePostOutsourcerInvoicePublishConfirm();
  const getAttachedFile = useGetAttachedFile();

  const [file, setFile] = React.useState<DownloadAttachedResult>();
  React.useState(() => {
    refetchDeadlines();
    onChangeChanger(monthState);
  });
  React.useEffect(() => {
    const newState =
      generateInitialState(searchParams, ['year', 'month']) ||
      state.pageInfo.INVOICE?.dateState;
    onChangeChanger(newState);
    setState(newState);
  }, [searchParams]);
  const fileObject = React.useMemo(
    () => ({
      url: TypeUtils.isNilNumber(data?.id)
        ? undefined
        : FileUtils.toFileDownloadUrl({ mode: FileKind.Invoice, id: data?.id }),
      httpHeaders: getRequestHeaders(getToken()),
      withCredentials: true,
    }),
    [data?.id]
  );
  React.useEffect(() => {
    if (
      !postOutsourcerInvoicePublishConfirm.isLoading &&
      TypeUtils.isNonNilNumber(data?.id)
    ) {
      getAttachedFile.mutate([{ mode: FileKind.Invoice, id: data?.id }], {
        onSuccess: (results) => {
          if (Array.isArray(results)) {
            setFile(results[0]);
          }
        },
      });
    }
  }, [postOutsourcerInvoicePublishConfirm.isLoading, data?.id]);

  return (
    <Wireframe title={pageNames.INVOICE} topMessages={deadlines?.results}>
      <VStack width={sizes.width.styledMessageList}>
        <MonthChanger
          onChangeLeft={onChangeChanger}
          onChangeRight={onChangeChanger}
        />
        <Skeleton isLoaded={!postOutsourcerInvoiceId.isLoading} width="100%">
          {TypeUtils.isNilString(fileObject.url) ||
          TypeUtils.isNil(file?.file) ? (
            <></>
          ) : (
            <>
              {TypeUtils.isNilNumber(data?.id) ||
              TypeUtils.isNilNumber(data?.version) ? (
                '請求書が発行されていません'
              ) : TypeUtils.isNil(file?.file) ? (
                '請求書が取得できません'
              ) : (
                <Image src={URL.createObjectURL(file!.file!)} />
              )}
              <DownloadButton
                marginY={2}
                query={{
                  mode: FileKind.Invoice,
                  id: data?.id,
                }}
              />
            </>
          )}
          <CheckArea
            additionalArea={
              <VStack>
                <Text color="red" fontWeight="bold">
                  !!! 注意 !!! 一度確定すると原則訂正できません
                </Text>
                <Text color="red" fontWeight="bold">
                  必ず入力内容を確認の上、間違いのないことを確認してから確定してください
                </Text>
              </VStack>
            }
            attension="請求内容に問題がある場合は、事務宛に別途ご連絡ください"
            button={DecideButton}
            buttonProps={{
              isDisabled: !checked || TypeUtils.isNilString(data?.id),
            }}
            confirmMessage="内容を確認しました．この請求書内容に同意します"
            doneMessage={
              TypeUtils.isNonNilNumber(data?.id) &&
              TypeUtils.isNonNilString(data?.confirmTime)
                ? '請求書確認が完了しています'
                : undefined
            }
            hidden={
              TypeUtils.isNilNumber(data?.id) ||
              TypeUtils.isNilNumber(data?.version)
            }
            // hiddenMessage="請求書が発行されていません"
            onClick={() => {
              if (
                TypeUtils.isNonNilNumber(data?.id) &&
                TypeUtils.isNonNilNumber(data?.version)
              ) {
                actions.setGuard(true);
                postOutsourcerInvoicePublishConfirm.mutate(
                  {
                    id: data?.id,
                    version: data?.version,
                  },
                  {
                    onSuccess: () => {
                      refetchDeadlines();
                      navigate();
                      actions.setGuard(false);
                    },
                    onError: (error) => {
                      toast(
                        error instanceof AppError
                          ? error
                          : '請求書確認に失敗しました'
                      );
                      actions.setGuard(false);
                    },
                  }
                );
              }
            }}
          />
        </Skeleton>
        <HStack width="100%">
          <BackButton onClick={() => navigate(-1)} />
        </HStack>
      </VStack>
      {/* <DateGroupChanger
        {...ym}
        leftDeadend={leftDeadend}
        rightDeadend={rightDeadend}
        onChange={setYm}
      />
      <Document
        file={url}
        onLoadSuccess={(pdf) =>
          setPdfState({ page: pdf.numPages, hasError: false })
        }
        onLoadError={() => setPdfState({ ...pdfState, hasError: true })}
      >
        {pdfState.hasError ? '' : <Page pageNumber={1} renderMode="svg" />}
      </Document>
      <VStack
        backgroundColor="white"
        width="100%"
        border="1px"
        padding={3}
        fontSize={sizes.fontSize.listMessage}
      >
        <Text color="red" fontWeight="bold">
          !!! 注意 !!! 一度確定すると原則訂正できません
        </Text>
        <Text color="red" fontWeight="bold">
          必ず入力内容を確認の上、間違いのないことを確認してから確定してください
        </Text>
        <Checkbox checked={checked} onChange={toggle} padding={3}>
          <Text fontSize={sizes.fontSize.confirmMessage}>
            内容を確認しました。この請求書内容に同意します
          </Text>
        </Checkbox>
        <StyledButton isDisabled={!checked}>請求確定</StyledButton>
        <Text color="red" fontWeight="bold">
          ※ 請求内容に問題がある場合は、事務宛に別途ご連絡ください
        </Text>
      </VStack> */}
    </Wireframe>
  );
};

export default Invoice;
